<template>
    <div class="flex flex-wrap gap-2">
        <button v-for="opt in options" :key="String(opt.value)" type="button"
            :data-testid="opt.testid" :aria-pressed="opt.value === modelValue ? 'true' : 'false'"
            :aria-disabled="isBlocked(opt) ? 'true' : undefined"
            class="h-10 px-4 rounded-full border text-sm transition-colors flex items-center gap-1.5"
            :class="pillClass(opt)"
            @click="onTap(opt)">
            <span v-if="opt.emoji" aria-hidden="true">{{ opt.emoji }}</span>
            <span>{{ opt.label }}</span>
            <HarmoniaPlusBadge v-if="opt.premium" />
            <span v-if="opt.soon"
                class="text-[10px] font-semibold uppercase tracking-wide rounded-full bg-warm-2 text-ink-3 px-1.5 py-0.5">
                {{ $t('tasks.form.soon') }}
            </span>
        </button>
    </div>
</template>

<script setup lang="ts">
import HarmoniaPlusBadge from '@/components/premium/HarmoniaPlusBadge.vue';

export interface PillOption {
    value: string | number;
    label: string;
    emoji?: string;
    soon?: boolean;
    disabled?: boolean;
    // Affiche le badge Harmonia+ après le libellé. Ne bloque PAS la sélection à
    // lui seul : le blocage reste piloté par `soon`/`disabled` (isBlocked).
    premium?: boolean;
    testid?: string;
}

const props = defineProps<{
    options: PillOption[];
    modelValue: string | number | null;
    deselectable?: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: string | number | null): void;
    (e: 'blockedTap', value: string | number): void;
}>();

const isBlocked = (opt: PillOption) => !!(opt.soon || opt.disabled);

const pillClass = (opt: PillOption) => {
    const selected = opt.value === props.modelValue;
    // Sélectionnée mais verrouillée : garder un style « choisi » reconnaissable
    // (teinté, atténué) plutôt que le style bloqué générique.
    if (selected && isBlocked(opt)) return 'bg-brand border-brand text-ink opacity-60';
    if (isBlocked(opt)) return 'bg-warm border-line text-ink-3 opacity-60';
    if (selected) return 'bg-brand border-brand text-ink font-medium';
    return 'bg-white border-line text-ink-2 active:bg-warm';
};

const onTap = (opt: PillOption) => {
    if (isBlocked(opt)) { emit('blockedTap', opt.value); return; }
    if (opt.value === props.modelValue) {
        if (props.deselectable) emit('update:modelValue', null);
        return;
    }
    emit('update:modelValue', opt.value);
};
</script>
